import { useEffect, useState, useCallback, useRef } from 'react';

export interface ServiceWorkerState {
  isReady: boolean;
  isUpdateAvailable: boolean;
  isWaitingForActivation: boolean;
  isChecking: boolean;
}

export function useServiceWorker() {
  const [state, setState] = useState<ServiceWorkerState>({
    isReady: false,
    isUpdateAvailable: false,
    isWaitingForActivation: false,
    isChecking: false,
  });

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const skipWaiting = useCallback(() => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SKIP_WAITING',
      });
      // Reload page to activate new service worker
      window.location.reload();
    }
  }, []);

  // Manual check for updates
  const checkForUpdates = useCallback(async () => {
    if (!registrationRef.current) return;

    setState((prev) => ({ ...prev, isChecking: true }));

    try {
      await registrationRef.current.update();
    } catch (error) {
      console.error('Failed to check for updates:', error);
    } finally {
      setState((prev) => ({ ...prev, isChecking: false }));
    }
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const handleControllerChange = () => {
      setState((prev) => ({
        ...prev,
        isWaitingForActivation: false,
      }));
    };

    const handleSWMessage = (event: MessageEvent) => {
      if (event.data.type === 'SYNC_SUCCESS') {
        // Handle sync success notification if needed
        console.log('Offline request synced:', event.data.requestUrl);
      }
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    navigator.serviceWorker.addEventListener('message', handleSWMessage);

    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        registrationRef.current = registration;

        setState((prev) => ({
          ...prev,
          isReady: true,
        }));

        // Check if there's already a waiting worker (from previous visit)
        if (registration.waiting) {
          setState((prev) => ({
            ...prev,
            isUpdateAvailable: true,
            isWaitingForActivation: true,
          }));
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is ready, but not activated
              setState((prev) => ({
                ...prev,
                isUpdateAvailable: true,
                isWaitingForActivation: true,
              }));
            }
          });
        });

        // Check for updates immediately on load
        registration.update().catch(() => {
          // Silently fail - not critical
        });
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    // Check for updates when app becomes visible (user returns to tab/app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && registrationRef.current) {
        registrationRef.current.update().catch(() => {
          // Silently fail
        });
      }
    };

    // Check for updates when coming back online
    const handleOnline = () => {
      if (registrationRef.current) {
        registrationRef.current.update().catch(() => {
          // Silently fail
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return {
    ...state,
    skipWaiting,
    checkForUpdates,
  };
}
